/* =========================================================================
   acete — product form

   Options are rendered by Liquid, one button per UNIQUE option value. This
   resolves the selected combination against the real variant list, disables
   values that do not exist in the current selection, and adds through the AJAX
   cart.

   The thing this is built to avoid: rendering one button per VARIANT. A product
   with 16 colours and 8 sizes has 128 of them, which is six "S" buttons in a
   row and no way to tell which is which.
   ========================================================================= */

/* No bare specifiers. They go through the import map, which carries absolute
   protocol-relative CDN urls, and those break the moment the page is not https.
   The cart comes off the global acete.js publishes; the gallery is a sibling
   file, so a relative path resolves from this module's own url and is correct
   on the CDN and on a dev server alike. */
const shop = () => window.acete || {};
const money = (c) => (shop().money ? shop().money(c) : "$" + (c / 100).toFixed(2));

let createGallery = () => null;
let markThumbs = () => {};
try {
  ({ createGallery, markThumbs } = await import("./gallery.js"));
} catch (e) {
  // the photograph will still change, it just will not cross-fade
  console.warn("acete: gallery unavailable, images swap without a fade", e);
}

const form = document.querySelector("[data-product-form]");
const json = document.querySelector("[data-product-json]");
if (form && json) {
  const product = JSON.parse(json.textContent);
  const groups = [...form.querySelectorAll("[data-option-group]")];
  const addBtn = form.querySelector("[data-add]");
  const msg = form.querySelector("[data-addmsg]");
  const priceEl = document.querySelector("[data-price]");
  const mainImg = document.querySelector("[data-main-img]");
  // Every route into a different photograph goes through one staged cross-fade
  // rather than rewriting src under the shopper. See gallery.js.
  const plate = createGallery(document.querySelector("[data-main]"));
  const thumbRail = document.querySelector("[data-thumbs]");
  const showImage = (src) => { if (src) { plate?.show(src); markThumbs(thumbRail, src); } };

  /* The thumbnails are `image_url: width: 1200`, so a variant photograph has to
     be asked for the same way — the legacy `_1200x.jpg` filename form names a
     different URL for the same file, and then no thumbnail ever matches the
     plate. */
  const sized = (url, w = 1200) => url + (url.includes("?") ? "&" : "?") + "width=" + w;

  const say = (t) => { if (msg) msg.textContent = t || " "; };

  // Supplier colour names are free text ("Vintage Blue", "Dusty Rose"), so the
  // swatch reads the words rather than showing every chip in the same grey.
  const NAMED = {
    black: "#16161a", white: "#EFEBE2", ivory: "#EFEBE2", cream: "#EFEBE2",
    bone: "#C3B9A6", oat: "#C3B9A6", sand: "#C3B9A6", khaki: "#B3A382",
    beige: "#C9BFAC", taupe: "#B0A392", stone: "#BFB7A8",
    grey: "#6E6E74", gray: "#6E6E74", charcoal: "#3A3A40", slate: "#5A626B",
    navy: "#242F45", indigo: "#2B3556", blue: "#4C6785", denim: "#4C6785",
    green: "#4A5140", olive: "#4A5140", sage: "#7E8770", army: "#4A5140",
    brown: "#6B5344", coffee: "#4F3B30", camel: "#A6825A", mocha: "#6B5344",
    red: "#8A3A30", rust: "#8A4E38", brick: "#8A4E38", burgundy: "#5C2730",
    wine: "#5C2730", maroon: "#5C2730", pink: "#C08B92", rose: "#C08B92",
    blush: "#D8B4B0", purple: "#5E4C6B", lavender: "#8E82A3", violet: "#5E4C6B",
    yellow: "#B08E3C", mustard: "#A8862F", gold: "#A8862F",
    orange: "#B4642F", apricot: "#C68B5A", turquoise: "#3F8C89", teal: "#2F6B68",
    mauve: "#8B6F7A", peacock: "#1F5E6E", moss: "#5A6348",
  };
  function colourOf(name) {
    const words = String(name).toLowerCase().split(/[^a-z]+/).filter(Boolean);
    for (let i = words.length - 1; i >= 0; i--) if (NAMED[words[i]]) return NAMED[words[i]];
    return "#4A4A50";
  }

  const selection = () => groups.map((g) => {
    const on = g.querySelector('[aria-pressed="true"]');
    return on ? on.dataset.optionValue : null;
  });

  const matches = (v, sel) =>
    sel.every((val, i) => val === null || v.options[i] === val);

  const findVariant = (sel) =>
    product.variants.find((v) => v.options.every((o, i) => o === sel[i]));

  /* A value is offerable when at least one available variant matches it
     together with every OTHER currently-selected value. */
  function refresh() {
    const sel = selection();

    groups.forEach((g, gi) => {
      g.querySelectorAll("[data-option-value]").forEach((btn) => {
        const test = sel.slice();
        test[gi] = btn.dataset.optionValue;
        const ok = product.variants.some((v) => v.available && matches(v, test));
        if (btn.classList.contains("size")) btn.disabled = !ok;
        btn.setAttribute("aria-disabled", String(!ok));
        if (!ok) btn.setAttribute("title", btn.dataset.optionValue + " — unavailable");
        else btn.removeAttribute("title");
      });
    });

    const variant = findVariant(sel);
    const name = form.querySelector("[data-colour-name]");
    const colourIdx = groups.findIndex((g) => g.classList.contains("swatches"));
    if (name && colourIdx > -1 && sel[colourIdx]) name.textContent = sel[colourIdx];

    if (variant) {
      if (priceEl) priceEl.textContent = money(variant.price);
      // a colour is a picture, not a word. Shopify puts the colourway shot on
      // the variant, so show it the moment the swatch is picked.
      if (mainImg && variant.featured_image) showImage(sized(variant.featured_image.src));
      const url = new URL(location);
      url.searchParams.set("variant", variant.id);
      history.replaceState({}, "", url);
    }

    addBtn.disabled = !product.variants.some((v) => v.available);
    if (addBtn.disabled) addBtn.textContent = "Sold out";
    return variant;
  }

  groups.forEach((g) => {
    // paint the swatches now that the values are known
    if (g.classList.contains("swatches")) {
      g.querySelectorAll("[data-option-value]").forEach((b) => {
        b.style.background = colourOf(b.dataset.optionValue);
      });
    }
    g.addEventListener("click", (e) => {
      const b = e.target.closest("[data-option-value]");
      if (!b || b.disabled) return;
      g.querySelectorAll("[data-option-value]").forEach((x) =>
        x.setAttribute("aria-pressed", String(x === b)));
      say("");
      refresh();
    });
  });

  refresh();

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const sel = selection();
    if (sel.some((v) => v === null)) { say("Choose every option first."); return; }
    const variant = findVariant(sel);
    if (!variant) { say("That combination is not available."); return; }
    if (!variant.available) { say("That one is sold out."); return; }

    addBtn.disabled = true;
    const label = addBtn.textContent;
    addBtn.textContent = "Adding…";
    try {
      await shop().Cart.add(variant.id, 1);
      say("Added to cart.");
      window.aceteOpenCart?.();
    } catch (err) {
      say(err.message || "Could not add that.");
    } finally {
      addBtn.textContent = label;
      addBtn.disabled = false;
    }
  });

  /* gallery */
  thumbRail?.addEventListener("click", (e) => {
    const b = e.target.closest("[data-src]");
    if (!b || !mainImg) return;
    showImage(b.dataset.src);
  });
}
